import { contentDelete, contentGet, contentPost } from '../email-activity/content-api'
import { queryString } from '../sso-workspace/auth-api'

// SCIM provisioning's admin routes on rapid-api-content (spec
// 2026-10-23-scim-provisioning-design R22), under
// /api/ddd/scim/connections. An organization acts on its own link to a
// connection (ORG_SSO); EducationXR staff name the organization with
// ?organization_id= (SYS_PROVIDER). Each call resolves to the body and, on a
// refusal, throws the response ({ status, data: { code, message } }), so
// serverMessage(err) reads content's own sentence.
//
// A link row: { connection: { id, name, public, google, ownedByOrganization },
// organization: { id, name }, available, reason ('public' | 'not_configured'
// | null), configured, planOn, enabled, baseURL, lastRequestAt, token: { id,
// prefix, status ('active' | 'expiring' | 'expired'), expiresAt, lastUsedAt,
// createdAt, rateLimitPerMinute, createdBySupport, createdBy } | null,
// authMethod ('bearer' | 'oauth' | null), oauth: { clientId, tokenEndpoint,
// secretPrefix, retiring: { prefix, endsAt } | null, tokensIssued,
// lastTokenAt, createdAt } | null, oauthAvailable }.
//
// Turning provisioning on, rotating and switching the method answer once:
// { token, baseURL, authMethod, secret } for the Bearer token, or { token,
// baseURL, authMethod, clientId, clientSecret, tokenEndpoint } for OAuth
// (spec R26). The secret is handed straight to the caller and kept nowhere
// here.

const base = providerID => `scim/connections/${encodeURIComponent(providerID)}`
const forOrg = organizationID => (organizationID ? { organization_id: organizationID } : undefined)

// The caller's organization's links to non-public connections.
export const listOwnLinks = () => contentGet('scim/connections')

// Every organization's link to one connection (staff).
export const listConnectionLinks = providerID => contentGet(`${base(providerID)}/links`)

// One link: the caller's own, or (staff) organizationID's.
export const getLink = (providerID, organizationID) => contentGet(base(providerID), forOrg(organizationID))

// Provisioning on: expiresAt is an RFC 3339 time, or null for a token that
// never expires. authMethod: 'bearer' (the default) or 'oauth'.
export const turnOn = (providerID, expiresAt, organizationID, authMethod) =>
  contentPost(`${base(providerID)}/token${queryString(forOrg(organizationID))}`,
    authMethod && authMethod !== 'bearer'
      ? { expires_at: expiresAt === undefined ? null : expiresAt, auth_method: authMethod }
      : { expires_at: expiresAt === undefined ? null : expiresAt })

// Provisioning off: the token stops on its next request. An optional reason.
export const revokeToken = (providerID, reason, organizationID) => {
  const text = String(reason || '').trim()
  return contentDelete(`${base(providerID)}/token`, forOrg(organizationID), text ? { reason: text } : {})
}

// A new secret for the organization's own token; the old one keeps working
// for graceHours (0 to 168). Staff can't rotate: they revoke and turn on
// again. expiresAt: undefined keeps the current expiry (the field is left
// out); an RFC 3339 time, or null for never, gives the new token that one
// ({ grace_hours, expires_at? }; content answers the new token, its
// expiresAt included).
export const rotateToken = (providerID, graceHours, expiresAt) =>
  contentPost(`${base(providerID)}/token/rotate`, expiresAt === undefined ? { grace_hours: graceHours } : { grace_hours: graceHours, expires_at: expiresAt })

// The organization's own link switches how its identity provider
// authenticates (authMethod 'bearer' or 'oauth'); the old credential keeps
// working for graceHours (0 to 168); expiresAt as rotateToken's.
export const switchMethod = (providerID, authMethod, graceHours, expiresAt) =>
  contentPost(`${base(providerID)}/token/method`, expiresAt === undefined
    ? { auth_method: authMethod, grace_hours: graceHours }
    : { auth_method: authMethod, grace_hours: graceHours, expires_at: expiresAt })

// { events: [{ at, action, data }], failures: [{ at, method, route, status, code }] }
export const getActivity = (providerID, organizationID) => contentGet(`${base(providerID)}/activity`, forOrg(organizationID))
